'use client';

import * as React from 'react';
import Link from 'next/link';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AgencyLogoHover } from './agency-hover-card';
import { ContactButtons } from './contact-buttons';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { OwnerProfileHoverCard } from '@/doi-tac-module/components/viec-lam/owner-profile-hover-card';

interface AgencyContactButtonsProps {
    agencyId?: string;
    preloadedAgency?: any;
    displayConsultant: any;
    language: string;
    chatText?: string;
    showChatText?: boolean;
    job?: any;
    hideTrigger?: boolean;
    isOwnerMode?: boolean;
    ownerUser?: any;
}

export function AgencyContactButtons({
    agencyId,
    preloadedAgency,
    displayConsultant,
    language,
    chatText: manualChatText,
    showChatText = true,
    job,
    hideTrigger = false,
    isOwnerMode = false,
    ownerUser
}: AgencyContactButtonsProps) {
    const { role: viewerRole } = useAuth();

    // Normalize logic to detect Agent/Sales Manager role
    // Priority: If displayConsultant is a Master (Strategic Partner), we use its role to trigger Master UI.
    const isConsultantMaster = displayConsultant?.role === 'MASTER_CTV' || displayConsultant?.role === 'master';
    const targetRole = isConsultantMaster ? displayConsultant.role : (preloadedAgency?.role || displayConsultant?.role);
    const isTargetMaster = targetRole === 'MASTER_CTV' || targetRole === 'master';

    // Logic to determine labels and IDs based on Viewer Role (User's specific request)
    const isForcingMaster = viewerRole === 'CTV';
    const isForcingAgency = viewerRole === 'MASTER_CTV' || viewerRole === 'CTV' || viewerRole === 'company' || viewerRole === 'guest';

    // Automatically determine chat text based on roles
    let resolvedChatText = manualChatText;
    const isMasterProfile = displayConsultant?.role === 'MASTER_CTV' || displayConsultant?.role === 'master';

    if (!resolvedChatText) {
        if (isForcingMaster || isMasterProfile) {
            resolvedChatText = (agencyTranslations.candidateSearch as any).card.buttons.contactMaster[language] || 'Liên hệ Tổng đại lý';
        } else if (isForcingAgency) {
            resolvedChatText = (agencyTranslations.candidateSearch as any).card.buttons.contactAgency[language] || 'Liên hệ Đại lý';
        } else {
            // Default fallback based on target recruiter role
            resolvedChatText = isTargetMaster
                ? (agencyTranslations.candidateSearch as any).card.buttons.contactMaster[language] || 'Liên hệ Tổng đại lý'
                : (agencyTranslations.candidateSearch as any).card.buttons.contactAgency[language] || 'Liên hệ Đại lý';
        }
    }

    // Determine container id for tracking/styling
    let containerId = "agencyContactButtons"; // Generic default
    if (isForcingMaster || isMasterProfile) {
        containerId = "agencyContactButtons_MASTER";
    } else if (isForcingAgency) {
        containerId = "agencyContactButtons_AGENCY";
    } else {
        // Fallback to target role if viewer is not an agency role
        containerId = isTargetMaster ? "AgencyContactButtons_MASTER" : "AgencyContactButtons_AGENT";
    }

    return (
        <div className="flex items-center gap-1.5 min-w-0">
            {displayConsultant && (() => {
                // Priority: If displayConsultant is forced (e.g. Strategic Partner/Master), use its data. 
                // Unlike standard advisors, we want Master info to override the agency representative on these pages.
                const isMasterDisplay = displayConsultant?.role === 'MASTER_CTV' || displayConsultant?.role === 'master';
                
                const avatarUrl = isMasterDisplay
                    ? displayConsultant.avatarUrl 
                    : (preloadedAgency?.representativePhoto || displayConsultant?.avatarUrl);
                
                const avatarName = isMasterDisplay
                    ? displayConsultant.name
                    : (preloadedAgency?.representativeName || displayConsultant?.name || '');

                const avatarContent = (
                    <Avatar className="h-8 w-8 border border-slate-200 shrink-0 shadow-sm transition-transform hover:scale-110">
                        <AvatarImage src={avatarUrl} alt={avatarName} />
                        <AvatarFallback className="bg-slate-100 text-[10px]">
                            {avatarName.charAt(0) || 'A'}
                        </AvatarFallback>
                    </Avatar>
                );

                return (
                    <>
                        {hideTrigger ? (
                            avatarContent
                        ) : isOwnerMode && ownerUser ? (
                            <OwnerProfileHoverCard
                                user={ownerUser}
                                language={language}
                                job={job}
                                trigger={
                                    <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0 cursor-pointer">
                                        {avatarContent}
                                    </div>
                                }
                            />
                        ) : (
                            <AgencyLogoHover
                                agencyId={isMasterDisplay ? (displayConsultant.id || displayConsultant.uid) : (agencyId ?? '')}
                                initialData={isMasterDisplay ? displayConsultant : preloadedAgency}
                                language={language}
                                align="start"
                                layout="avatar"
                                trigger={
                                    <Link href={agencyId ? `/cac-dai-ly/${agencyId}` : '#'} className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                        {avatarContent}
                                    </Link>
                                }
                            />
                        )}
                        <div className="flex-grow min-w-0 [&_span.ml-2]:hidden xl:[&_span.ml-2]:inline-block text-left">
                            <ContactButtons
                                id={containerId}
                                contact={{
                                    ...(displayConsultant as any),
                                    role: targetRole
                                }}
                                showChatText={showChatText}
                                chatText={resolvedChatText}
                                job={job}
                            />
                        </div>
                    </>
                );
            })()}
        </div>
    );
}
